import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, ChevronUp, ChevronDown, AlertTriangle, Link2 } from "lucide-react";

import { MIN_PER_DAY, formatHM, pad2 } from "@/lib/time";
import { getCategory, tint, CONFLICT_COLOR, PARENT_INDICATOR_COLOR } from "@/lib/categories";
import { shouldTransferToPage } from "@/lib/calendarGestures";
import { useTranslation } from "@/i18n";

// Windowing config per resolution (block size in minutes × how many blocks are visible).
//  15min → 8 blocks (2h window) · 1h → 8 blocks (8h) · 2h → 8 blocks (16h)
//  24h (1440) → 12 blocks of 2h = the WHOLE day (00:00–24:00), no scrolling.
const WINDOW_CFG = {
  15: { block: 15, count: 8 },
  60: { block: 60, count: 8 },
  120: { block: 120, count: 8 },
  1440: { block: 120, count: 12 },
};

const TRACK_HEIGHT = 480; // fixed visible height; block height derives from visible block count

function fmtMinutes(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${pad2(h)}:${pad2(m)}`;
}

/**
 * TimelineBar — vertical, fixed-window timeline.
 * Shows exactly `count` blocks for the active resolution and snaps by one block
 * per wheel/swipe/arrow. Event data never changes on scroll — only the window.
 */
export default function TimelineBar({
  title,
  subtitle,
  resolutionMinutes,
  events,
  overlapSegments,
  onEventClick,
  onOverlapClick,
  testIdPrefix,
  nowMinutes,
  scrollToMinutes,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const trackRef = useRef(null);
  const wheelLock = useRef(0);
  const touchStartY = useRef(null);
  const touchStartX = useRef(null);

  const cfg = WINDOW_CFG[resolutionMinutes] || WINDOW_CFG[15];
  const windowMinutes = cfg.block * cfg.count;
  const snapStep = cfg.block;
  const maxStart = Math.max(0, MIN_PER_DAY - windowMinutes);
  const blockPx = TRACK_HEIGHT / cfg.count;
  const pxPerMin = blockPx / cfg.block;

  const snapDown = useCallback(
    (mins) => {
      const clamped = Math.min(Math.max(mins, 0), maxStart);
      return Math.floor(clamped / snapStep) * snapStep;
    },
    [maxStart, snapStep]
  );

  const [windowStart, setWindowStart] = useState(() => snapDown(scrollToMinutes ?? 8 * 60));

  // Re-align window when resolution or day changes.
  useEffect(() => {
    setWindowStart(snapDown(scrollToMinutes ?? 8 * 60));
  }, [resolutionMinutes, scrollToMinutes, snapDown]);

  const windowEnd = windowStart + windowMinutes;

  const step = useCallback(
    (dir) => {
      setWindowStart((prev) => {
        const next = prev + dir * snapStep;
        return Math.min(Math.max(next, 0), maxStart);
      });
    },
    [snapStep, maxStart]
  );

  const canUp = windowStart > 0;
  const canDown = windowStart < maxStart;
  // Latest boundary state reachable from the native listeners attached once below.
  const boundaryRef = useRef({ canUp, canDown });
  boundaryRef.current = { canUp, canDown };

  // Wheel + touch snapping (one block per gesture). Non-passive so we can control
  // page scroll: a vertical drag steps the window and blocks page scroll UNTIL the
  // window hits its top/bottom boundary, at which point a continued drag in that
  // direction transfers naturally to the outer page (spec §2). Horizontal drags are
  // left for the day/scale handler on <main>.
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const onWheel = (e) => {
      e.preventDefault();
      const now = Date.now();
      if (now - wheelLock.current < 220) return;
      if (Math.abs(e.deltaY) < 4) return;
      wheelLock.current = now;
      step(e.deltaY > 0 ? 1 : -1);
    };
    const onTouchStart = (e) => {
      touchStartY.current = e.touches[0]?.clientY ?? null;
      touchStartX.current = e.touches[0]?.clientX ?? null;
    };
    const onTouchMove = (e) => {
      if (touchStartY.current == null) return;
      const tch = e.touches[0];
      if (!tch) return;
      const moveY = tch.clientY - touchStartY.current;
      const moveX = tch.clientX - (touchStartX.current ?? tch.clientX);
      // Horizontal-dominant → let <main>'s day/scale gesture own it.
      if (Math.abs(moveX) > Math.abs(moveY)) return;
      const transfer = shouldTransferToPage({
        moveY,
        canUp: boundaryRef.current.canUp,
        canDown: boundaryRef.current.canDown,
      });
      // Keep the vertical drag inside the timeline unless we're at the boundary in
      // the drag direction (then allow the page to scroll).
      if (!transfer && Math.abs(moveY) > 4 && e.cancelable) e.preventDefault();
    };
    const onTouchEnd = (e) => {
      if (touchStartY.current == null) return;
      const endY = e.changedTouches[0]?.clientY ?? touchStartY.current;
      const endX = e.changedTouches[0]?.clientX ?? (touchStartX.current ?? 0);
      const dy = touchStartY.current - endY;                 // >0 finger up → later
      const dx = (touchStartX.current ?? endX) - endX;
      // Only step for a clearly-vertical drag past threshold; horizontal is handled
      // by <main> (day/scale), so ignore horizontal-dominant gestures here.
      if (Math.abs(dy) > 24 && Math.abs(dy) >= Math.abs(dx)) step(dy > 0 ? 1 : -1);
      touchStartY.current = null;
      touchStartX.current = null;
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("wheel", onWheel);
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [step]);

  // Block grid lines / labels within the window.
  const gridLines = useMemo(() => {
    const arr = [];
    for (let i = 0; i <= cfg.count; i++) {
      const minute = windowStart + i * cfg.block;
      arr.push({ minute, y: i * blockPx });
    }
    return arr;
  }, [cfg.count, cfg.block, windowStart, blockPx]);

  // Only events overlapping the current window are drawn.
  const visibleEvents = useMemo(
    () => events.filter((ev) => ev.endMinutes > windowStart && ev.startMinutes < windowEnd),
    [events, windowStart, windowEnd]
  );

  // Ids of events that are a PARENT of at least one child in this day. A parent
  // renders in a reduced LEFT lane so its title/time stay readable, and its
  // children render in a right lane at their real time (never covering the parent).
  const parentIds = useMemo(() => {
    const s = new Set();
    for (const ev of events) if (ev.parentEventId) s.add(ev.parentEventId);
    return s;
  }, [events]);

  return (
    <div
      className="surface-card overflow-hidden"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-center justify-between px-4 sm:px-5 pt-3 sm:pt-5 pb-2 sm:pb-3 gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-lg sm:text-xl font-semibold text-slate-900 truncate">{title}</h3>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-500 mt-0.5 sm:mt-1">{subtitle}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            className="text-xs font-mono-tab text-slate-700 bg-slate-100 rounded-full px-2.5 py-1"
            data-testid={`${testIdPrefix}-window-range`}
          >
            {fmtMinutes(windowStart)}–{fmtMinutes(windowEnd)}
          </span>
          <span className="text-xs text-slate-500 font-mono-tab" data-testid={`${testIdPrefix}-count`}>
            {events.length} {events.length === 1 ? t("timeline.event") : t("timeline.events")}
          </span>
        </div>
      </div>

      <div className="relative flex">
        {/* Up / down snap controls — always above the event layer so they never scroll underneath cards */}
        <div className="absolute right-2 top-2 z-[70] flex flex-col gap-1">
          <button
            onClick={() => step(-1)}
            disabled={!canUp}
            className="h-8 w-8 rounded-full bg-white border border-slate-300 shadow-sm flex items-center justify-center text-slate-700 disabled:opacity-30 hover:bg-slate-50 active:scale-95 transition"
            data-testid={`${testIdPrefix}-scroll-up`}
            aria-label={t("timeline.earlier")}
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            onClick={() => step(1)}
            disabled={!canDown}
            className="h-8 w-8 rounded-full bg-white border border-slate-300 shadow-sm flex items-center justify-center text-slate-700 disabled:opacity-30 hover:bg-slate-50 active:scale-95 transition"
            data-testid={`${testIdPrefix}-scroll-down`}
            aria-label={t("timeline.later")}
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        <div
          ref={trackRef}
          className="timeline-window relative w-full overflow-hidden select-none outline-none focus-visible:ring-2 focus-visible:ring-slate-300 rounded-bl-[20px]"
          style={{ height: `${TRACK_HEIGHT}px` }}
          data-testid={`${testIdPrefix}-scroller`}
          data-window-start={windowStart}
          tabIndex={0}
          role="slider"
          aria-label={t("timeline.window")}
          aria-valuemin={0}
          aria-valuemax={maxStart}
          aria-valuenow={windowStart}
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "PageUp") {
              e.preventDefault();
              step(-1);
            } else if (e.key === "ArrowDown" || e.key === "PageDown") {
              e.preventDefault();
              step(1);
            }
          }}
        >
          <div className="relative" style={{ height: `${TRACK_HEIGHT}px` }} data-testid={`${testIdPrefix}-track`}>
            {/* Permanent white time-column background: keeps the time labels readable
                and stops event cards bleeding into the time area while scrolling. */}
            <div
              className="absolute left-0 top-0 bottom-0 w-16 bg-white border-r border-slate-200/70"
              data-testid={`${testIdPrefix}-time-column`}
              aria-hidden
            />
            {/* Block grid lines + start-of-block labels */}
            {gridLines.map(({ minute, y }, i) => (
              <div
                key={`g-${i}`}
                className="absolute left-0 right-0 flex items-start"
                style={{ top: `${y}px` }}
              >
                <div className="w-16 shrink-0 pl-3 -translate-y-2 text-[11px] font-mono-tab text-slate-500">
                  {fmtMinutes(Math.min(minute, MIN_PER_DAY))}
                </div>
                <div className="flex-1 border-t border-slate-200/60" />
              </div>
            ))}

            {/* Event blocks (clipped to the window) */}
            <div className="absolute inset-0 pl-16 pr-3">
              {visibleEvents.map((ev) => {
                const segStart = Math.max(ev.startMinutes, windowStart);
                const segEnd = Math.min(ev.endMinutes, windowEnd);
                const top = (segStart - windowStart) * pxPerMin;
                const rawH = (segEnd - segStart) * pxPerMin;
                const height = Math.max(20, rawH);
                const cat = getCategory(ev.category);
                const timeLabel = `${formatHM(ev.startHour, ev.startMinute)}–${formatHM(ev.endHour, ev.endMinute)}`;
                const crosses = ev._continuesNext || ev._continuedPrev;
                const isChild = !!ev.parentEventId;
                const isParent = parentIds.has(ev.id);
                // Lane layout: parent keeps a readable LEFT lane; children sit in a
                // right lane at their real time so they never cover the parent text.
                const leftStyle = isChild ? "55%" : "64px";
                const rightStyle = isParent ? "46%" : isChild ? "10px" : "12px";
                const segLabel = crosses
                  ? `${fmtMinutes(ev.startMinutes)}–${fmtMinutes(ev.endMinutes)}`
                  : timeLabel;
                const compact = height < 34;
                return (
                  <button
                    key={ev.id}
                    onClick={() => onEventClick?.(ev)}
                    className="btn-lift absolute rounded-[14px] text-left overflow-hidden bg-card border shadow-sm"
                    style={{
                      top: `${top}px`,
                      height: `${height}px`,
                      left: leftStyle,
                      right: rightStyle,
                      backgroundColor: tint(cat.hex, 0.13),
                      borderColor: isChild ? PARENT_INDICATOR_COLOR : tint(cat.hex, 0.32),
                      boxShadow: isChild ? `0 0 0 1.5px ${tint(PARENT_INDICATOR_COLOR, 0.55)}` : undefined,
                      color: cat.ink,
                      // Children render OVER their parent so the relationship reads clearly.
                      zIndex: 10 + (ev.priority || 1) + (isChild ? 30 : 0),
                    }}
                    data-testid={`${testIdPrefix}-event-${ev.id}`}
                    data-cross-midnight={crosses ? "true" : undefined}
                    data-child-event={isChild ? "true" : undefined}
                    data-parent-event={isParent ? "true" : undefined}
                    aria-label={`${ev.title} ${timeLabel}`}
                  >
                    <span
                      className="absolute inset-y-0 left-0 w-1.5"
                      style={{ backgroundColor: cat.hex }}
                      aria-hidden
                    />
                    {isChild ? (
                      <span
                        className="absolute inset-y-0 left-1.5 w-1"
                        style={{ backgroundColor: PARENT_INDICATOR_COLOR }}
                        aria-hidden
                      />
                    ) : null}
                    <div className={["pl-3 pr-2 py-1 h-full flex flex-col", isParent ? "justify-start" : "justify-center"].join(" ")}>
                      {compact ? (
                        <div className="flex items-center gap-1.5 leading-tight">
                          {isChild ? (
                            <Link2 className="h-3 w-3 shrink-0" style={{ color: PARENT_INDICATOR_COLOR }} aria-hidden />
                          ) : null}
                          <span className="text-[12px] font-semibold truncate">{ev.title}</span>
                          {crosses ? (
                            <span className="text-[9px] font-mono-tab font-semibold rounded px-1 shrink-0" style={{ backgroundColor: tint(cat.hex, 0.3) }}>
                              {ev._continuesNext ? "→ +1" : "+1 →"}
                            </span>
                          ) : null}
                          <span className="text-[10px] font-mono-tab opacity-70 shrink-0 ml-auto">
                            {segLabel}
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5 leading-tight">
                            {isChild ? (
                              <Link2 className="h-3 w-3 shrink-0" style={{ color: PARENT_INDICATOR_COLOR }} aria-hidden />
                            ) : null}
                            <span className="text-[12px] font-semibold truncate">{ev.title}</span>
                            {crosses ? (
                              <span className="text-[9px] font-mono-tab font-semibold rounded px-1 shrink-0" style={{ backgroundColor: tint(cat.hex, 0.3) }}>
                                {ev._continuesNext ? "→ +1 day" : "prev day →"}
                              </span>
                            ) : null}
                          </div>
                          <div className="text-[11px] font-mono-tab opacity-75 leading-tight">{segLabel}</div>
                          {height > 56 && ev.description ? (
                            <div className="text-[11px] opacity-60 truncate mt-0.5">{ev.description}</div>
                          ) : null}
                        </>
                      )}
                    </div>
                  </button>
                );
              })}

              {/* Overlap segments (clipped) */}
              {overlapSegments
                .filter((seg) => seg.end > windowStart && seg.start < windowEnd)
                .map((seg, idx) => {
                  const s = Math.max(seg.start, windowStart);
                  const e = Math.min(seg.end, windowEnd);
                  const top = (s - windowStart) * pxPerMin;
                  const height = Math.max(8, (e - s) * pxPerMin);
                  const isBlack = seg.count >= 3;
                  return (
                    <div
                      key={`ov-${idx}`}
                      className="absolute left-0 right-1 rounded-r-md flex items-start justify-start pointer-events-none"
                      style={{
                        top: `${top}px`,
                        height: `${height}px`,
                        backgroundColor: isBlack ? "rgba(30,30,30,0.72)" : tint(CONFLICT_COLOR, 0.62),
                        zIndex: 50,
                        boxShadow: isBlack ? "inset 3px 0 0 #1E1E1E" : `inset 3px 0 0 ${CONFLICT_COLOR}`,
                      }}
                      data-testid={`${testIdPrefix}-overlap-${idx}`}
                      data-overlap-count={seg.count}
                    >
                      <button
                        onClick={() => onOverlapClick?.(seg)}
                        className="m-1 rounded-full bg-white/95 text-slate-900 hover:bg-white shadow ring-1 ring-black/10 p-1 transition-transform active:scale-95 pointer-events-auto"
                        aria-label={t("timeline.overlapAria", { count: seg.count })}
                        data-testid={`${testIdPrefix}-overlap-btn-${idx}`}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" strokeWidth={2.5} />
                      </button>
                    </div>
                  );
                })}

              {/* Now indicator (only when inside the window) */}
              {nowMinutes != null && nowMinutes >= windowStart && nowMinutes <= windowEnd ? (
                <div
                  className="absolute left-0 right-0 pointer-events-none"
                  style={{ top: `${(nowMinutes - windowStart) * pxPerMin}px`, zIndex: 60 }}
                  data-testid={`${testIdPrefix}-now-indicator`}
                >
                  <div className="flex items-center">
                    <div className="w-16 pr-2 flex justify-end">
                      <span className="text-[10px] font-mono-tab font-semibold text-rose-400 bg-card px-1 rounded">
                        {t("timeline.now")}
                      </span>
                    </div>
                    <div className="flex-1 h-px bg-rose-300" />
                    <div className="h-1.5 w-1.5 rounded-full bg-rose-400 -ml-1" />
                  </div>
                </div>
              ) : null}

              {/* Empty hint */}
              {events.length === 0 && overlapSegments.length === 0 ? (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="text-center max-w-[220px] text-slate-400 px-6">
                    <p className="text-sm">{t("timeline.noEvents")}</p>
                    <button
                      onClick={() => navigate("/create")}
                      className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-slate-700 hover:text-slate-900 pointer-events-auto"
                      data-testid={`${testIdPrefix}-empty-create`}
                    >
                      {t("timeline.addFirst")} <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
